/**
 * Logistics P&L — Ad-hoc Bookings.
 * Enter Porter/Rapido transport events (beneficiary facility + amount + date). On save the
 * cost flows into that facility's logistics cost + the EBITDA Logistics line immediately —
 * no extra step. Lists the facility's bookings for a date range. City-scoped.
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useFacilities } from '../../manpower-workers/hooks/useFacilities';
import { useBookings, useCreateBooking } from '../hooks/useLogistics';
import type { Booking } from '../../../services/logisticsApi';

const todayIso = () => {
	const n = new Date();
	return new Date(n.getTime() - n.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};
const monthStart = () => `${todayIso().slice(0, 7)}-01`;

export const AdhocBookings: React.FC = () => {
	const user = useSelector((state: RootState) => state.auth.user);
	const { data: facilities = [] } = useFacilities(user?.city_id ?? undefined);

	const [facilityId, setFacilityId] = useState<number | ''>('');
	const [from] = useState(monthStart());
	const [to] = useState(todayIso());

	// Add-booking form
	const [platform, setPlatform] = useState('Porter');
	const [amount, setAmount] = useState('');
	const [bookingDate, setBookingDate] = useState(todayIso());
	const [category, setCategory] = useState('transport');
	const [toast, setToast] = useState<string | null>(null);

	const { data: bookings = [], isLoading, error } = useBookings(
		facilityId === '' ? undefined : facilityId,
		from,
		to
	);
	const create = useCreateBooking();

	// Auto-select the (city-scoped) facility once loaded.
	useEffect(() => {
		if (facilityId === '' && facilities.length > 0) setFacilityId(facilities[0].id);
	}, [facilityId, facilities]);

	const showToast = (m: string) => {
		setToast(m);
		window.setTimeout(() => setToast(null), 3500);
	};

	const amountNum = Number(amount);
	const canAdd = facilityId !== '' && amount !== '' && amountNum > 0 && /^\d{4}-\d{2}-\d{2}$/.test(bookingDate);

	const handleAdd = async () => {
		try {
			await create.mutateAsync({
				beneficiary_facility_id: facilityId as number,
				platform,
				amount: amountNum,
				booking_date: bookingDate,
				category,
			});
			showToast('Booking saved — EBITDA updated');
			setAmount('');
		} catch (e) {
			showToast(e instanceof Error ? e.message : 'Failed to save booking');
		}
	};

	const inputCls = 'rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none';
	const rows: Booking[] = bookings;

	return (
		<div className='min-h-screen bg-white p-6'>
			<div className='max-w-7xl mx-auto'>
				<div className='mb-6'>
					<h1 className='text-xl font-semibold text-gray-900'>Ad-hoc Bookings</h1>
					<p className='text-sm text-gray-500'>
						Logistics P&amp;L — log Porter/Rapido transport. Cost hits the facility&apos;s EBITDA on save.
					</p>
				</div>

				{/* Facility + add-booking form */}
				<div className='mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 p-4'>
					<div className='flex flex-col gap-1'>
						<label className='text-xs font-medium text-gray-700'>Beneficiary facility</label>
						<select value={facilityId} onChange={e => setFacilityId(e.target.value === '' ? '' : Number(e.target.value))} className={`${inputCls} w-56`}>
							<option value=''>Select a facility…</option>
							{facilities.map(f => <option key={f.id} value={f.id}>{f.location}</option>)}
						</select>
					</div>
					<div className='flex flex-col gap-1'>
						<label className='text-xs font-medium text-gray-700'>Platform</label>
						<input type='text' value={platform} onChange={e => setPlatform(e.target.value)} placeholder='Porter / Rapido' className={`${inputCls} w-32`} />
					</div>
					<div className='flex flex-col gap-1'>
						<label className='text-xs font-medium text-gray-700'>Amount (₹) <span className='text-red-500'>*</span></label>
						<input type='number' min={1} value={amount} onChange={e => setAmount(e.target.value)} placeholder='e.g. 800' className={`${inputCls} w-28`} />
					</div>
					<div className='flex flex-col gap-1'>
						<label className='text-xs font-medium text-gray-700'>Date <span className='text-red-500'>*</span></label>
						<input type='date' value={bookingDate} onChange={e => setBookingDate(e.target.value)} className={inputCls} />
					</div>
					<div className='flex flex-col gap-1'>
						<label className='text-xs font-medium text-gray-700'>Category</label>
						<input type='text' value={category} onChange={e => setCategory(e.target.value)} className={`${inputCls} w-32`} />
					</div>
					<button type='button' onClick={handleAdd} disabled={!canAdd || create.isPending} className='rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50'>
						{create.isPending ? 'Saving…' : '+ Add booking'}
					</button>
				</div>

				{/* Bookings list (this month) */}
				<div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
					<div className='overflow-x-auto'>
						<table className='w-full border-collapse'>
							<thead>
								<tr className='bg-gray-50 border-b border-gray-200'>
									<th className='px-4 py-3 text-left text-xs font-bold text-gray-900'>Date</th>
									<th className='px-4 py-3 text-left text-xs font-bold text-gray-900'>Platform</th>
									<th className='px-4 py-3 text-left text-xs font-bold text-gray-900'>Category</th>
									<th className='px-4 py-3 text-right text-xs font-bold text-gray-900'>Amount</th>
									<th className='px-4 py-3 text-left text-xs font-bold text-gray-900'>Flag</th>
								</tr>
							</thead>
							<tbody>
								{facilityId === '' ? (
									<tr><td colSpan={5} className='px-4 py-8 text-center text-gray-500 text-sm'>Select a facility.</td></tr>
								) : isLoading ? (
									<tr><td colSpan={5} className='px-4 py-8 text-center text-gray-500 text-sm'>Loading bookings…</td></tr>
								) : error ? (
									<tr><td colSpan={5} className='px-4 py-8 text-center text-red-600 text-sm'>Failed to load: {error.message}</td></tr>
								) : rows.length === 0 ? (
									<tr><td colSpan={5} className='px-4 py-8 text-center text-gray-500 text-sm'>No bookings this month.</td></tr>
								) : (
									rows.map((b, i) => (
										<tr key={b.id ?? i} className='border-b border-gray-200 hover:bg-gray-50'>
											<td className='px-4 py-2 text-xs text-gray-900'>{b.booking_date}</td>
											<td className='px-4 py-2 text-xs text-gray-900'>{b.platform ?? '—'}</td>
											<td className='px-4 py-2 text-xs text-gray-900'>{b.category ?? '—'}</td>
											<td className='px-4 py-2 text-xs text-gray-900 text-right'>₹{Number(b.amount ?? 0).toLocaleString('en-IN')}</td>
											<td className='px-4 py-2'>
												{b.city_mismatch === 1 && (
													<span className='inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700'>cross-city</span>
												)}
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{toast && <div className='fixed bottom-6 right-6 z-50 rounded-md bg-gray-900 px-4 py-2 text-sm text-white shadow-lg'>{toast}</div>}
		</div>
	);
};

export default AdhocBookings;
